import { BellRing, ExternalLink, Info, RotateCcw, ShieldCheck, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { showAdPrivacyOptions } from '../lib/ads'
import {
  cancelNativeNotifications,
  createNotificationPlan,
  isNativeApp,
  notificationStorageKey,
  requestNativeNotificationPermission,
  syncNativeNotifications,
  type NotificationSettings,
} from '../lib/notifications'
import type { ProgramContent } from '../types/content'

interface SettingsViewProps {
  program: ProgramContent
}

export default function SettingsView({ program }: SettingsViewProps) {
  const [settings, setSettings] = useLocalStorage<NotificationSettings>(notificationStorageKey, {
    start: false,
    sevenDays: false,
    oneDay: false,
  })
  const [permissionMessage, setPermissionMessage] = useState('')

  const nativeApp = isNativeApp()
  const notificationSupported = nativeApp || typeof Notification !== 'undefined'

  const toggleNotification = async (key: keyof NotificationSettings) => {
    const enabling = !settings[key]
    const nextSettings = { ...settings, [key]: enabling }

    if (nativeApp) {
      try {
        const plan = createNotificationPlan(program.application, nextSettings)
        if (enabling && plan.length > 0) {
          const granted = await requestNativeNotificationPermission()
          if (!granted) {
            setPermissionMessage('알림 권한이 허용되지 않았습니다. 기기 설정에서 변경할 수 있습니다.')
            return
          }
        }
        setSettings(nextSettings)
        const result = await syncNativeNotifications(program.application, nextSettings)
        setPermissionMessage(
          result.scheduled > 0
            ? `${result.scheduled}개의 신청 알림을 기기에 예약했습니다.`
            : '공식 신청 일정이 확정되면 선택한 알림을 기기에 예약합니다.',
        )
      } catch {
        setPermissionMessage('알림을 설정하지 못했습니다. 잠시 후 다시 시도해 주세요.')
      }
      return
    }

    if (enabling && notificationSupported && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setPermissionMessage('알림 권한이 허용되지 않았습니다. 브라우저 또는 기기 설정에서 변경할 수 있습니다.')
        return
      }
    }
    if (enabling && notificationSupported && Notification.permission === 'denied') {
      setPermissionMessage('알림이 차단되어 있습니다. 브라우저 또는 기기 설정에서 권한을 허용해 주세요.')
      return
    }
    setPermissionMessage('')
    setSettings(nextSettings)
  }

  const resetData = async () => {
    const confirmed = window.confirm('체크리스트와 알림 설정을 모두 초기화할까요? 이 작업은 되돌릴 수 없습니다.')
    if (!confirmed) return
    await cancelNativeNotifications().catch(() => undefined)
    Object.keys(localStorage)
      .filter((key) => key.startsWith('gg-transport'))
      .forEach((key) => localStorage.removeItem(key))
    window.location.reload()
  }

  const openAdPrivacyOptions = async () => {
    try {
      await showAdPrivacyOptions()
      setPermissionMessage('광고 개인정보 선택 화면을 확인했습니다.')
    } catch {
      setPermissionMessage('현재 변경할 수 있는 광고 개인정보 옵션이 없습니다.')
    }
  }

  return (
    <div className="view-stack">
      <section className="settings-section" aria-labelledby="notification-title">
        <div className="settings-section__heading"><BellRing aria-hidden="true" /><div><p className="eyebrow">알림</p><h2 id="notification-title">신청 일정을 놓치지 마세요</h2></div></div>
        {!notificationSupported && <p className="info-message">웹 브라우저 알림을 지원하지 않는 환경입니다. Android 앱에서는 로컬 알림을 제공할 예정입니다.</p>}
        {permissionMessage && <p className="info-message" role="status">{permissionMessage}</p>}
        <div className="settings-list">
          <label><span><strong>신청 시작 알림</strong><small>신청 기간이 시작되면 알려드려요.</small></span><input type="checkbox" checked={settings.start} onChange={() => void toggleNotification('start')} /></label>
          <label><span><strong>마감 7일 전</strong><small>준비할 시간을 넉넉하게 확보하세요.</small></span><input type="checkbox" checked={settings.sevenDays} onChange={() => void toggleNotification('sevenDays')} /></label>
          <label><span><strong>마감 1일 전</strong><small>마지막으로 신청 여부를 확인하세요.</small></span><input type="checkbox" checked={settings.oneDay} onChange={() => void toggleNotification('oneDay')} /></label>
        </div>
        <p className="privacy-note">공식 신청 일정이 등록된 경우에만 알림을 예약합니다.</p>
      </section>

      <section className="settings-section" aria-labelledby="legal-title">
        <div className="settings-section__heading"><ShieldCheck aria-hidden="true" /><div><p className="eyebrow">정보 보호</p><h2 id="legal-title">개인정보와 출처</h2></div></div>
        <div className="legal-notice"><Info aria-hidden="true" /><p>{program.disclaimer}</p></div>
        <div className="link-list">
          <a href="/privacy.html">개인정보처리방침 <ExternalLink aria-hidden="true" size={16} /></a>
          {nativeApp && (
            <button type="button" className="settings-link-button" onClick={() => void openAdPrivacyOptions()}>
              광고 개인정보 설정 <SlidersHorizontal aria-hidden="true" size={16} />
            </button>
          )}
          <a href={program.sourceUrl} target="_blank" rel="noreferrer">공식 출처 확인 <ExternalLink aria-hidden="true" size={16} /></a>
          <a href="/testers.html">비공개 테스트 참여 <ExternalLink aria-hidden="true" size={16} /></a>
          <a href="/support.html">앱 지원 <ExternalLink aria-hidden="true" size={16} /></a>
        </div>
      </section>

      <button type="button" className="button button--danger" onClick={() => void resetData()}>
        <RotateCcw aria-hidden="true" size={17} />
        저장 데이터 초기화
      </button>
    </div>
  )
}
